import React, { useContext, useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import URI from "urijs";
import { Routes } from "../../app";
import {
  getBeneficiaryInfo,
  makePlanLongId,
  logError,
  fetchCurrentPlan,
  isOutsideOpenEnrollment,
} from "../../helpers";
import { AppContext } from "../../app/store";
import { ActionType, UserLanguage, FullPlanId, Envs } from "../../@types";
import { getBeneDrugs, getCounties, getCounty } from "../../api";
import { useFlags } from "launchdarkly-react-client-sdk";
import { fetchPlanIdForNextYear } from "../../helpers/planHelpers";
import { getPlanDetailsStarRatingsRoute } from "../../helpers";
import { isCurrentPlan } from "../../helpers/beneficiaryInfoHelpers";
import { LoadingMask } from "../../components";
import {
  parseDegradedIntegrations,
  initiateLogin,
  getEnvironment,
} from "../../helpers/loginHelpers";

enum Targets {
  ENROLL_STATUS = "ENROLL_STATUS",
  GATEWAY = "GATEWAY",
  PLAN = "PLAN",
  PRESCRIPTIONS = "PRESCRIPTIONS",
  STAR_RATINGS = "STAR_RATINGS",
}

export interface MBPLandingPageProps {
  code?: string;
  req_token?: string;
  target?: string;
  planYear?: string;
  contractId?: string;
  planId?: string;
  segmentId?: string;
  fips?: string;
  plan_type?: string;
  zipCode?: string | number;
  lang?: string;
  relay?: string;
}

const MBPLandingPage: React.FunctionComponent<RouteComponentProps> = ({
  location: { search },
  history,
}) => {
  const {
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  } = useFlags();

  const {
    dispatch,
    state: { language },
  } = useContext(AppContext);

  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });

  const handleMbpFailure = (): void => {
    dispatch({ type: ActionType.UPDATE_MBP_HANDOFF_FAILED, payload: true });
    history.replace("/");
  };

  const getRouteDetails = async (): Promise<void> => {
    dispatch({ type: ActionType.UPDATE_MBP_HANDOFF_FAILED, payload: false });

    const tempRoute = new URI("/");

    const searchObj = URI.parseQuery(search) as MBPLandingPageProps;

    const code = searchObj.req_token;

    // If we are using SLSx and there isn't a req_token, we need to send the user to SSO
    if (!code) {
      const isLocal = getEnvironment() === Envs.local;

      initiateLogin(
        language === UserLanguage.SPANISH,
        isLocal
          ? "https://localhost/mbp"
          : `${window.location.origin}/plan-compare/mbp`,
        searchObj
      );

      return;
    }

    let params: MBPLandingPageProps = {};

    const { relay } = searchObj;

    if (relay) {
      try {
        // relay should be a base64-encoded string
        const decodedRelay: MBPLandingPageProps = JSON.parse(atob(relay));

        params = decodedRelay;
      } catch (e) {
        logError("Failed to parse the relay (MBPLandingPage)", e);

        handleMbpFailure();
        return;
      }
    }

    const {
      fips,
      zipCode,
      target,
      planYear: contract_year,
      planId: plan_id,
      contractId: contract_id,
      segmentId: segment_id,
      lang = UserLanguage.ENGLISH,
    } = params;

    if (!code || !target) {
      handleMbpFailure();
      return;
    }

    let finalFips = fips;

    dispatch({ type: ActionType.UPDATE_LANGUAGE, payload: lang });

    if (!fips && zipCode) {
      dispatch({ type: ActionType.UPDATE_ZIPCODE, payload: zipCode });

      await getCounties(String(zipCode)).then(counties => {
        if (counties.length === 1) {
          finalFips = counties[0].fips;
          dispatch({ type: ActionType.UPDATE_COUNTY, payload: counties[0] });
        }
      });
    }

    if (finalFips) {
      dispatch({ type: ActionType.UPDATE_FIPS, payload: finalFips });
      await getCounty(finalFips).then(county =>
        dispatch({ type: ActionType.UPDATE_COUNTY, payload: county })
      );
    }

    let nextYearPlanId: FullPlanId | undefined;

    try {
      const { headers, beneficiary: bene } = await getBeneficiaryInfo(code);

      const degradedIntegrations = parseDegradedIntegrations(headers);

      if (degradedIntegrations.length) {
        dispatch({
          type: ActionType.UPDATE_DEGRADED_INTEGRATIONS,
          payload: degradedIntegrations,
        });
      }

      if (bene) {
        dispatch({ type: ActionType.ADD_BENEFICIARY, payload: bene });
        dispatch({
          type: ActionType.UPDATE_LIS,
          payload: bene.lis_level,
        });

        if (bene.current_coverage.length) {
          if (!outsideOpenEnrollment) {
            nextYearPlanId = await fetchPlanIdForNextYear(
              bene.current_coverage[0],
              "MBP Landing Page"
            );
          }
          fetchCurrentPlan(
            bene,
            [],
            bene.lis_level,
            dispatch,
            nextYearPlanId,
            bene.future_lis_level
          );
        }

        try {
          const drugs = await getBeneDrugs();

          dispatch({
            type: ActionType.SET_PRESCRIPTIONS,
            payload: drugs,
          });

          if (target === Targets.PRESCRIPTIONS) {
            let route = "";
            if (drugs.length > 0) {
              route = Routes.prescriptionsList;
            } else if (bene.pdes.length > 0) {
              route = Routes.pdePage;
            } else {
              route = Routes.managePrescriptions;
            }

            tempRoute.href(`${route}?fips=${finalFips ? finalFips : ""}`);
          }
        } catch (e) {
          logError("Could not get beneficiary's drugs (MBPLandingPage)", e);

          if (target === Targets.PRESCRIPTIONS) {
            handleMbpFailure();
            return;
          }
        }

        if (target === Targets.PLAN || target === Targets.STAR_RATINGS) {
          if (contract_year && plan_id && segment_id && contract_id) {
            const idParts = { contract_year, plan_id, segment_id, contract_id };
            // If we are not outside open enrollment and the id passed in is different from
            // what we have as the bene's current coverage, we defer to the passed
            // in id when determining the id of the next year's plan
            if (
              !outsideOpenEnrollment &&
              !isCurrentPlan(bene.current_coverage[0], idParts)
            ) {
              nextYearPlanId = await fetchPlanIdForNextYear(
                idParts,
                "MBP Landing Page"
              );
            }
            const urlPlanId = nextYearPlanId
              ? makePlanLongId(nextYearPlanId)
              : makePlanLongId(idParts);

            if (target === Targets.PLAN) {
              tempRoute.href(Routes.planDetails.replace(":id", urlPlanId));
            } else if (target === Targets.STAR_RATINGS) {
              tempRoute.href(
                getPlanDetailsStarRatingsRoute(
                  nextYearPlanId ? nextYearPlanId : idParts,
                  language,
                  contract_year
                )
              );
            }
          } else {
            handleMbpFailure();
            return;
          }
        }
      }

      if (target === Targets.GATEWAY) {
        tempRoute.href(Routes.beneLanding);
      }

      if (target === Targets.ENROLL_STATUS) {
        tempRoute.href(Routes.enrollmentStatus);
      }

      history.replace(tempRoute.addQuery({ lang }).toString());
    } catch (e) {
      logError("Could not get beneficiary (MBPLandingPage)", e);
      handleMbpFailure();
    }
  };

  useEffect(() => {
    dispatch({ type: ActionType.RESET_STATE });

    getRouteDetails();
  }, []);

  return <LoadingMask loading={true} />;
};

export default withRouter(MBPLandingPage);
